/**
 * « Modifier l'intervention » — UN panneau pour corriger ce qu'on a saisi (U6, né du retrait de l'ancien planning : l'ancienne
 * visite se corrigeait par des menus ; le nouvel écran ne savait que glisser).
 *
 * Même esprit que « Nouvelle intervention » : un seul enregistrement, rien n'est écrit tant qu'on n'enregistre pas, et on n'envoie
 * QUE ce qui a changé (`domaine/modification-intervention.ts`, testé). Si quelqu'un d'autre a corrigé l'intervention entre-temps,
 * le serveur répond 409 : on relit derrière le panneau, PUIS on le dit — rien n'est écrasé. Fermer un panneau touché demande
 * confirmation.
 */
import { useEffect, useMemo, useRef, useState } from 'react';

import { ApiError, api, type Asset, type BriefingIntervention, type NatureBon, type Technicien } from '../api/client';
import {
  ajouterUnJour,
  basculerTechnicien,
  formulaireDepuis,
  formulaireModifie,
  joursDeLaPlage,
  modificationDeLIntervention,
  phraseModification,
  questionJoursAvecHeures,
  retirerUnJour,
  type FormulaireModification,
} from '../domaine/modification-intervention';
import { jourCourt, machineChoisie } from '../domaine/planning';
import { Champ, ErreurFormulaire, Panneau } from './Formulaire';

const MACHINES_DU_SITE = 100;

export function ModifierIntervention({
  briefing,
  onFermer,
  onModifiee,
  onConflit,
}: {
  briefing: BriefingIntervention;
  onFermer: () => void;
  /** L'écriture a réussi : la page relit, puis dit ce message. */
  onModifiee: (message: string) => void;
  /** Le serveur a refusé (409) : l'intervention a changé depuis l'ouverture. La page relit derrière le panneau. */
  onConflit: () => void;
}) {
  const i = briefing.intervention;
  const depart = useRef(formulaireDepuis(briefing));
  const [f, setF] = useState<FormulaireModification>(depart.current.formulaire);
  const ctx = depart.current.contexte;

  const [natures, setNatures] = useState<readonly NatureBon[] | null>(null);
  const [techniciens, setTechniciens] = useState<readonly Technicien[] | null>(null);
  const [machines, setMachines] = useState<readonly Asset[] | null>(null);
  const [erreurChargement, setErreurChargement] = useState<string | null>(null);

  const [jourAjoute, setJourAjoute] = useState('');
  const [plageDu, setPlageDu] = useState('');
  const [plageAu, setPlageAu] = useState('');
  const [plageOuvres, setPlageOuvres] = useState(true);
  const [refusJours, setRefusJours] = useState<string | null>(null);

  const [tente, setTente] = useState(false);
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<ApiError | Error | null>(null);

  useEffect(() => {
    let annule = false;
    Promise.all([
      ctx.atelier ? Promise.resolve(null) : api.naturesBon(),
      api.techniciensPlanning(),
      ctx.machineAIdentifier && i.site ? api.assets({ siteId: i.site.id, take: MACHINES_DU_SITE }) : Promise.resolve(null),
    ])
      .then(([n, t, m]) => {
        if (annule) return;
        setNatures(n === null ? [] : n.filter((x) => !x.archived));
        setTechniciens(t.items.filter((x) => !x.archived));
        setMachines(m === null ? [] : m.items);
      })
      .catch((e: unknown) => {
        if (!annule) setErreurChargement(e instanceof ApiError ? e.message : 'Chargement impossible.');
      });
    return () => {
      annule = true;
    };
  }, [ctx.atelier, ctx.machineAIdentifier, i.site]);

  const resultat = useMemo(() => modificationDeLIntervention(depart.current.formulaire, f, ctx), [f, ctx]);
  const modifie = formulaireModifie(depart.current.formulaire, f);

  // Un technicien déjà dans l'équipe mais archivé depuis reste visible (et décochable) : il ne disparaît pas en silence.
  const noms = useMemo(() => {
    const m = new Map<string, string>();
    for (const j of briefing.jours) for (const x of j.members) m.set(x.technicianId, x.name);
    return m;
  }, [briefing.jours]);
  const listeTechniciens = useMemo(() => {
    const actifs = techniciens ?? [];
    const connus = new Set(actifs.map((t) => t.id));
    const archives = depart.current.formulaire.techniciens
      .filter((id) => !connus.has(id))
      .map((id) => ({ id, name: `${noms.get(id) ?? 'Technicien'} (archivé)` }));
    return [...actifs.map((t) => ({ id: t.id, name: t.name })), ...archives];
  }, [techniciens, noms]);

  const fermer = () => {
    if (enCours) return;
    if (modifie && !window.confirm('Abandonner les modifications non enregistrées ?')) return;
    onFermer();
  };

  const ajouterJour = () => {
    const jours = ajouterUnJour(f.jours, jourAjoute);
    if (jours === null) {
      setRefusJours('Choisis un jour dans le champ date.');
      return;
    }
    setRefusJours(null);
    setF({ ...f, jours });
    setJourAjoute('');
  };
  const ajouterPlage = () => {
    const plage = joursDeLaPlage(plageDu, plageAu, plageOuvres);
    if ('refus' in plage) {
      setRefusJours(plage.refus);
      return;
    }
    setRefusJours(null);
    setF({ ...f, jours: [...new Set([...f.jours, ...plage.jours])].sort() });
    setPlageDu('');
    setPlageAu('');
  };

  const enregistrer = async () => {
    setTente(true);
    if ('rien' in resultat || 'erreurs' in resultat) return;
    if (resultat.joursRetiresAvecHeures.length > 0 && !window.confirm(questionJoursAvecHeures(resultat.joursRetiresAvecHeures))) return;
    setEnCours(true);
    setErreur(null);
    try {
      await api.modifierIntervention(i.id, resultat.corps);
      onModifiee(phraseModification(resultat.corps, resultat.joursSansEquipe));
    } catch (e) {
      setEnCours(false);
      // 409 : l'intervention a changé depuis l'ouverture du panneau. On relit derrière, PUIS on le dit.
      if (e instanceof ApiError && e.status === 409) onConflit();
      setErreur(e instanceof ApiError || e instanceof Error ? e : new Error('Modification impossible.'));
    }
  };

  const messages = 'erreurs' in resultat ? resultat.erreurs : [];
  const sousTitre = [i.site?.name, i.asset?.label].filter(Boolean).join(' · ');

  return (
    <Panneau
      titre="Modifier l’intervention"
      {...(sousTitre === '' ? {} : { sousTitre })}
      onFermer={fermer}
      actions={
        <>
          <button type="button" disabled={enCours} className="secondaire" onClick={fermer}>
            Annuler
          </button>
          {/* Grisé tant que rien n'a changé ; actif même si une erreur est à dire (un clic la montre). */}
          <button type="button" disabled={enCours || 'rien' in resultat} onClick={() => void enregistrer()}>
            {enCours ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </>
      }
    >
      {erreurChargement && <div className="message erreur">{erreurChargement}</div>}

      {ctx.atelier ? (
        <Champ libelle="Titre" requis>
          <input type="text" maxLength={500} value={f.titre} onChange={(e) => setF({ ...f, titre: e.target.value })} aria-label="Titre" />
        </Champ>
      ) : (
        <Champ libelle="Nature" requis>
          <select value={f.natureId} onChange={(e) => setF({ ...f, natureId: e.target.value })} aria-label="Nature">
            {natures === null ? <option value={f.natureId}>Chargement…</option> : null}
            {natures !== null && !natures.some((n) => n.id === f.natureId) && f.natureId !== '' && (
              <option value={f.natureId}>{i.natureLabel ?? 'Nature actuelle'} (archivée)</option>
            )}
            {natures !== null && f.natureId === '' && <option value="">Choisir…</option>}
            {(natures ?? []).map((n) => (
              <option key={n.id} value={n.id}>
                {n.label}
              </option>
            ))}
          </select>
        </Champ>
      )}

      <Champ libelle="Ce que le client a dit">
        <textarea rows={3} maxLength={4000} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} aria-label="Ce que le client a dit" />
      </Champ>
      <Champ libelle="N° de commande du client">
        <input type="text" maxLength={200} value={f.commande} onChange={(e) => setF({ ...f, commande: e.target.value })} aria-label="N° de commande" />
      </Champ>
      <div className="champ">
        <label>
          <input type="checkbox" checked={f.tentative} onChange={(e) => setF({ ...f, tentative: e.target.checked })} /> À confirmer (pré-planifiée)
        </label>
      </div>

      <h3>Quand ?</h3>
      <div className="champ">
        <label>Jours travaillés</label>
        {f.jours.length === 0 ? (
          <p className="aide">Aucun jour : l’intervention attend dans la liste « À affecter ».</p>
        ) : (
          <div className="chips-secondaires" role="group" aria-label="Jours travaillés">
            {f.jours.map((j) => (
              <button key={j} type="button" className="chip actif" title="Retirer ce jour" onClick={() => setF({ ...f, jours: retirerUnJour(f.jours, j) })}>
                {jourCourt(j)} ✕
              </button>
            ))}
          </div>
        )}
        <div className="ligne-champs">
          <input type="date" value={jourAjoute} onChange={(e) => setJourAjoute(e.target.value)} aria-label="Jour à ajouter" />
          <button type="button" className="secondaire" onClick={ajouterJour}>
            + Ajouter ce jour
          </button>
        </div>
        <div className="ligne-champs">
          <label>
            du <input type="date" value={plageDu} onChange={(e) => setPlageDu(e.target.value)} aria-label="Début de la plage" />
          </label>
          <label>
            au <input type="date" value={plageAu} onChange={(e) => setPlageAu(e.target.value)} aria-label="Fin de la plage" />
          </label>
          <label>
            <input type="checkbox" checked={plageOuvres} onChange={(e) => setPlageOuvres(e.target.checked)} /> jours ouvrés
          </label>
          <button type="button" className="secondaire" onClick={ajouterPlage}>
            + Ajouter la plage
          </button>
        </div>
        {refusJours && <div className="message erreur">{refusJours}</div>}
        <p className="aide">Retirer un jour écarte son équipe. Un jour ajouté reprend l’équipe cochée plus bas.</p>
      </div>

      <div className="champ">
        <label>Échéance (facultative)</label>
        <div className="ligne-champs">
          <label>
            à partir du <input type="date" value={f.fenetreDebut} onChange={(e) => setF({ ...f, fenetreDebut: e.target.value })} aria-label="À partir du" />
          </label>
          <label>
            au plus tard le <input type="date" value={f.fenetreFin} onChange={(e) => setF({ ...f, fenetreFin: e.target.value })} aria-label="Au plus tard le" />
          </label>
        </div>
        <p className="aide">La date avant laquelle il faut l’avoir faite : elle sert au classement de la liste « À affecter ».</p>
      </div>

      <h3>Qui ?</h3>
      <div className="champ">
        {techniciens === null ? (
          <p className="aide">Chargement…</p>
        ) : (
          <div className="chips-secondaires" role="group" aria-label="Techniciens">
            {listeTechniciens.map((t) => {
              const actif = f.techniciens.includes(t.id);
              return (
                <button key={t.id} type="button" className={`chip${actif ? ' actif' : ''}`} aria-pressed={actif} onClick={() => setF({ ...f, techniciens: basculerTechnicien(f.techniciens, t.id) })}>
                  {t.name}
                </button>
              );
            })}
          </div>
        )}
        <p className="aide">
          Les techniciens cochés travaillent tous les jours listés. Un chef reste chef tant qu’il est coché ; on change de chef dans le
          briefing. Aucun coché = « à affecter ».
        </p>
        {!ctx.equipesIdentiques && (
          <p className="aide">
            Attention : l’équipe n’est pas la même tous les jours. Tant que tu ne touches pas aux cases, chaque jour garde la sienne ;
            dès que tu en changes une, la même équipe s’applique à tous les jours.
          </p>
        )}
      </div>

      <Champ libelle="Durée prévue, par technicien et par jour" aide="« 8 » = 8 h, « 7,5 », « 1h30 ». Une estimation, jamais un temps réel.">
        <input
          type="text"
          inputMode="decimal"
          value={f.duree}
          placeholder={ctx.dureeMixte ? 'Plusieurs durées : laisser vide = inchangé' : '8, 7,5, 1h30'}
          onChange={(e) => setF({ ...f, duree: e.target.value })}
          aria-label="Durée prévue"
        />
      </Champ>

      {ctx.machineAIdentifier && (
        <Champ libelle="Machine" aide="Cette intervention n’a pas encore de machine : choisis-la parmi celles du site. Une fois choisie, elle ne se remplace plus.">
          <select value={f.machineId} onChange={(e) => setF({ ...f, machineId: e.target.value })} aria-label="Machine">
            <option value="">Toujours à identifier</option>
            {(machines ?? []).map((a) => (
              <option key={a.id} value={a.id}>
                {machineChoisie(a).libelle}
              </option>
            ))}
          </select>
        </Champ>
      )}
      {!ctx.atelier && !ctx.machineAIdentifier && (
        <p className="aide">La machine ne se change pas : pour en changer, annule cette intervention et crée-en une autre.</p>
      )}

      {tente && messages.length > 0 && (
        <div className="message erreur">
          <ul className="liste-messages">
            {messages.map((m) => (
              <li key={m}>{m}</li>
            ))}
          </ul>
        </div>
      )}
      {erreur instanceof ApiError && erreur.status === 409 ? (
        <div className="message erreur">
          Cette intervention vient d’être modifiée par quelqu’un d’autre : ferme ce panneau et rouvre-le pour repartir de la version à jour. Rien n’a été écrasé.
        </div>
      ) : (
        <ErreurFormulaire message={erreur?.message ?? null} details={erreur instanceof ApiError ? erreur.details : undefined} />
      )}
    </Panneau>
  );
}
